// ==================== userStorage.ts ACTUALIZADO ====================
import {
  registerUser,
  loginUser,
  updateUserProfile,
  getAllUsers,
  markFirstLoginCompleted,
  updateClientInterests,
  UserData,
  LoginResponse,
  RegisterResponse,
  healthCheck,
} from '../services/api';

// ==================== INTERFACES ====================
export interface UserDataLegacy {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  country: string;
  timezone: string;
  role: 'client' | 'organizer' | 'admin';
  isVerified: boolean;
  isFirstLogin: boolean;
  interests?: string[];
  profession?: string;
  documentType?: string;
  documentNumber?: string;
  paymentMethods?: {
    cbu: string;
    mercadoPago: string;
    lemon: string;
    stripe: boolean;
  };
  createdAt: Date;
}

interface SessionData {
  user: UserData;
  token: string;
  expiresAt: string;
  lastActivity: string;
}

// ==================== CONFIGURACIÓN ====================
const STORAGE_KEYS = {
  SESSION: 'peekpass-session',
  TOKEN: 'peekpass-token',
  USER: 'peekpass-user'
};
const SESSION_DURATION_DAYS = 30;
const SESSION_DURATION_MS = SESSION_DURATION_DAYS * 24 * 60 * 60 * 1000;

// ==================== UTILIDADES DE STORAGE (LOCALSTORAGE + COOKIES) ====================
const cookieUtils = {
  set: (name: string, value: string, days: number = SESSION_DURATION_DAYS): void => {
    try {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `expires=${date.toUTCString()}`;
      // Secure flag solo en HTTPS
      const secureFlag = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax${secureFlag}`;
    } catch (error) {
      console.error('Error guardando en cookie:', error);
    }
  },

  get: (name: string): string | null => {
    try {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    } catch (error) {
      console.error('Error leyendo de cookie:', error);
      return null;
    }
  },

  remove: (name: string): void => {
    try {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    } catch (error) {
      console.error('Error eliminando cookie:', error);
    }
  }
};

const storageUtils = {
  set: (key: string, value: any): void => {
    try {
      const stringValue = JSON.stringify(value);
      // Guardar en localStorage
      localStorage.setItem(key, stringValue);
      // También guardar en cookie como respaldo (solo si es pequeño)
      if (stringValue.length < 4000) { // Límite típico de cookies
        cookieUtils.set(key, encodeURIComponent(stringValue), SESSION_DURATION_DAYS);
      }
    } catch (error) {
      console.error('Error guardando en storage:', error);
    }
  },

  get: <T>(key: string): T | null => {
    try {
      // Primero intentar desde localStorage
      const item = localStorage.getItem(key);
      if (item) {
        return JSON.parse(item);
      }

      // Si no está en localStorage, intentar desde cookies
      const cookieValue = cookieUtils.get(key);
      if (cookieValue) {
        const decoded = decodeURIComponent(cookieValue);
        const parsed = JSON.parse(decoded);
        // Restaurar en localStorage si se encuentra en cookies
        localStorage.setItem(key, decoded);
        return parsed;
      }

      return null;
    } catch (error) {
      console.error('Error leyendo de storage:', error);
      return null;
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
      cookieUtils.remove(key);
    } catch (error) {
      console.error('Error eliminando de storage:', error);
    }
  },

  clear: (): void => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
        cookieUtils.remove(key);
      });
    } catch (error) {
      console.error('Error limpiando storage:', error);
    }
  }
};

// ==================== CONVERSIÓN DE DATOS ====================
const convertToLegacyFormat = (apiUser: UserData): UserDataLegacy => ({
  id: apiUser.id,
  firstName: apiUser.firstName,
  lastName: apiUser.lastName,
  email: apiUser.email,
  password: '',
  country: apiUser.country,
  timezone: apiUser.timezone,
  role: apiUser.role,
  isVerified: apiUser.isVerified,
  isFirstLogin: apiUser.isFirstLogin,
  interests: apiUser.interests,
  profession: apiUser.profession,
  documentType: apiUser.documentType,
  documentNumber: apiUser.documentNumber,
  paymentMethods: apiUser.paymentMethods,
  createdAt: new Date(apiUser.createdAt)
});

// ==================== GESTIÓN DE SESIÓN ====================
export const saveUserSession = (user: UserData, token: string): void => {
  try {
    console.log('💾 Guardando sesión de usuario:', user.email);

	    const expiresAt = new Date(Date.now() + SESSION_DURATION_MS).toISOString();

    const sessionData: SessionData = {
      user,
      token,
      expiresAt,
      lastActivity: new Date().toISOString()
    };

    // Guardar sesión completa (localStorage + cookies)
    storageUtils.set(STORAGE_KEYS.SESSION, sessionData);

    // Guardar token por separado para fácil acceso (localStorage + cookies)
    storageUtils.set(STORAGE_KEYS.TOKEN, token);

    // Guardar usuario por separado para compatibilidad (localStorage + cookies)
    storageUtils.set(STORAGE_KEYS.USER, user);

    // Verificar que se guardó correctamente
    const verifySession = storageUtils.get<SessionData>(STORAGE_KEYS.SESSION);
    const verifyToken = storageUtils.get<string>(STORAGE_KEYS.TOKEN);
    const verifyUser = storageUtils.get<UserData>(STORAGE_KEYS.USER);

    console.log('✅ Sesión guardada exitosamente');
    console.log('🔍 Verificación de guardado:', {
      sessionSaved: !!verifySession,
      tokenSaved: !!verifyToken,
      userSaved: !!verifyUser,
      userEmail: verifyUser?.email
    });
  } catch (error) {
    console.error('❌ Error guardando sesión:', error);
    throw new Error('Error guardando sesión de usuario');
  }
};

export const getCurrentUser = (): UserData | null => {
  try {
    // Debug: Verificar directamente en localStorage
    const sessionRaw = localStorage.getItem(STORAGE_KEYS.SESSION);
    const userRaw = localStorage.getItem(STORAGE_KEYS.USER);
    const tokenRaw = localStorage.getItem(STORAGE_KEYS.TOKEN);

    console.log('🔍 getCurrentUser - Estado del localStorage:', {
      sessionRaw: sessionRaw ? 'existe' : 'no existe',
      userRaw: userRaw ? 'existe' : 'no existe',
      tokenRaw: tokenRaw ? 'existe' : 'no existe'
    });

    // Primero intentar obtener de la sesión
    const session = storageUtils.get<SessionData>(STORAGE_KEYS.SESSION);

    console.log('🔍 getCurrentUser - Sesión parseada:', session ? 'existe' : 'no existe');

    if (session) {
      // Verificar si la sesión no ha expirado
      const now = new Date();
      const expiresAt = new Date(session.expiresAt);
      const isValid = now < expiresAt;

      console.log('🔍 getCurrentUser - Validación de expiración:', {
        now: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        isValid: isValid
      });

      if (isValid) {
        // Actualizar última actividad
        session.lastActivity = new Date().toISOString();
        storageUtils.set(STORAGE_KEYS.SESSION, session);

        console.log('✅ getCurrentUser - Sesión válida, retornando usuario:', session.user?.email);
        return session.user;
      } else {
        console.log('⏰ Sesión expirada, limpiando...');
        clearUserSession();
        return null;
      }
    }

    // Fallback: intentar obtener usuario por separado
    // Si hay usuario y token guardados por separado, reconstruir la sesión
    const user = storageUtils.get<UserData>(STORAGE_KEYS.USER);
    const token = storageUtils.get<string>(STORAGE_KEYS.TOKEN);

    console.log('🔍 getCurrentUser - Fallback:', {
      userExists: !!user,
      tokenExists: !!token,
      userEmail: user?.email
    });

    if (user && token) {
      console.log('🔄 Reconstruyendo sesión desde datos guardados por separado');
      // Reconstruir la sesión completa
      try {
        const sessionData: SessionData = {
          user,
          token,
	          expiresAt: new Date(Date.now() + SESSION_DURATION_MS).toISOString(),
          lastActivity: new Date().toISOString()
        };
        storageUtils.set(STORAGE_KEYS.SESSION, sessionData);
        console.log('✅ Sesión reconstruida exitosamente');
        return user;
      } catch (reconstructError) {
        console.error('❌ Error reconstruyendo sesión:', reconstructError);
        // Si falla la reconstrucción, limpiar todo
        clearUserSession();
        return null;
      }
    } else if (user || token) {
      // Si solo hay uno de los dos, limpiar (sesión incompleta)
      console.log('⚠️ Sesión incompleta encontrada, limpiando...');
      clearUserSession();
      return null;
    }

    return null;
  } catch (error) {
    console.error('❌ Error obteniendo usuario actual:', error);
    // No limpiar la sesión inmediatamente en caso de error
    // Puede ser un error temporal de lectura
    return null;
  }
};

export const getCurrentToken = (): string | null => {
  try {
    // Debug: Verificar directamente en localStorage
    const sessionRaw = localStorage.getItem(STORAGE_KEYS.SESSION);
    const tokenRaw = localStorage.getItem(STORAGE_KEYS.TOKEN);

    console.log('🔍 getCurrentToken - Estado del localStorage:', {
      sessionRaw: sessionRaw ? 'existe' : 'no existe',
      tokenRaw: tokenRaw ? 'existe' : 'no existe'
    });

    // Primero intentar obtener de la sesión
    const session = storageUtils.get<SessionData>(STORAGE_KEYS.SESSION);

    console.log('🔍 getCurrentToken - Sesión parseada:', session ? 'existe' : 'no existe');
    if (session) {
      const now = new Date();
      const expiresAt = new Date(session.expiresAt);
      const isValid = now < expiresAt;

      console.log('⏰ Expiración de sesión:', session.expiresAt);
      console.log('⏰ Tiempo actual:', now.toISOString());
      console.log('⏰ Sesión válida:', isValid);

      if (isValid) {
        console.log('✅ Token obtenido de sesión válida');
        return session.token;
      } else {
        console.log('⏰ Sesión expirada en getCurrentToken');
      }
    }

    // Fallback: intentar obtener token por separado (solo si no hay sesión)
    if (!session) {
      const token = storageUtils.get<string>(STORAGE_KEYS.TOKEN);
      if (token) {
        console.log('⚠️ Token sin sesión encontrado, intentando usar token standalone');
        // Si hay token standalone, intentar reconstruir la sesión
        const user = storageUtils.get<UserData>(STORAGE_KEYS.USER);
        if (user) {
          console.log('🔄 Intentando reconstruir sesión desde getCurrentToken...');
          try {
            const sessionData: SessionData = {
              user,
              token,
	              expiresAt: new Date(Date.now() + SESSION_DURATION_MS).toISOString(),
              lastActivity: new Date().toISOString()
            };
            storageUtils.set(STORAGE_KEYS.SESSION, sessionData);
            console.log('✅ Sesión reconstruida desde getCurrentToken');
            return token;
          } catch (error) {
            console.error('❌ Error reconstruyendo sesión desde getCurrentToken:', error);
          }
        }
        return token;
      }
    }

    console.log('❌ No se pudo obtener token válido');
    return null;
  } catch (error) {
    console.error('❌ Error obteniendo token:', error);
    // No limpiar la sesión inmediatamente en caso de error
    return null;
  }
};

export const clearUserSession = (): void => {
  try {
    console.log('🧹 Limpiando sesión de usuario...');
    storageUtils.clear();
    console.log('✅ Sesión limpiada exitosamente');
  } catch (error) {
    console.error('❌ Error limpiando sesión:', error);
  }
};

export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  const token = getCurrentToken();

  if (!user || !token) {
    return false;
  }

  // Verificar que el token no esté expirado
  try {
    const session = storageUtils.get<SessionData>(STORAGE_KEYS.SESSION);
    if (session && new Date() >= new Date(session.expiresAt)) {
      clearUserSession();
      return false;
    }
  } catch (error) {
    console.error('Error verificando expiración:', error);
    return false;
  }

  return true;
};

// ==================== FUNCIONES PRINCIPALES ====================
export const getUsers = async (): Promise<UserDataLegacy[]> => {
  try {
    const response = await getAllUsers();
    return response.users.map(convertToLegacyFormat);
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
    return [];
  }
};

export const saveUser = async (userData: Omit<UserDataLegacy, 'id' | 'createdAt'>, recaptchaToken?: string): Promise<UserDataLegacy> => {
  try {
    console.log('👤 Registrando nuevo usuario:', userData.email);

    const response: RegisterResponse = await registerUser({
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      password: userData.password,
      country: userData.country,
      timezone: userData.timezone,
      role: userData.role === 'admin' ? 'client' : userData.role,
      profession: userData.profession,
      documentType: userData.documentType,
      documentNumber: userData.documentNumber,
      paymentMethods: userData.paymentMethods,
      recaptchaToken
    });

    const apiUser = response?.data?.user;
    const token = response?.data?.token;

    console.log('🔍 Respuesta completa del servidor:', response);
    console.log('🔍 Usuario recibido:', apiUser);
    console.log('🔍 Token recibido:', token ? `${token.substring(0, 20)}...` : 'null');

    if (!apiUser || !token) {
      console.error('❌ Usuario o token no recibido del servidor:', response);
      throw new Error('No se recibió información del usuario desde el servidor.');
    }

    // Guardar sesión automáticamente
    saveUserSession(apiUser, token);

    console.log('✅ Usuario registrado y sesión iniciada exitosamente');
    return convertToLegacyFormat(apiUser);
  } catch (error: any) {
    console.error('Error al guardar usuario:', error);

    // Si el usuario ya existe, lanzar error apropiado
	    if (error.message && (error.message.includes('Ya existe un usuario con este email') || error.message.includes('Ya existe una cuenta registrada'))) {
	      console.log('❌ Usuario ya existe en la base de datos');
	      throw new Error('Ya existe una cuenta registrada con ese correo electrónico.');
	    }

    throw new Error(error.message || 'Error desconocido al guardar usuario.');
  }
};

export const findUserByEmail = async (email: string): Promise<UserDataLegacy | null> => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser || currentUser.email !== email) {
      return null;
    }
    return convertToLegacyFormat(currentUser);
  } catch (error) {
    console.error('Error al buscar usuario por email:', error);
    return null;
  }
};

// ==================== LOGIN ====================
export const verifyCredentials = async (email: string, password: string, recaptchaToken?: string): Promise<UserDataLegacy | null> => {
  try {
    console.log('🔍 Verificando credenciales para:', email);

    const response: LoginResponse = await loginUser(email, password, recaptchaToken);

    if (!response || !response.data || !response.data.user || !response.data.token) {
      console.log('❌ Respuesta inválida del servidor');
      throw new Error('Respuesta inválida del servidor');
    }

    if (!response.data.user.id || !response.data.user.email) {
      console.log('❌ Datos de usuario inválidos en la respuesta');
      throw new Error('Datos de usuario inválidos');
    }

    // Guardar sesión automáticamente
    saveUserSession(response.data.user, response.data.token);

    console.log('✅ Credenciales verificadas y sesión iniciada exitosamente');
    return convertToLegacyFormat(response.data.user);

  } catch (error: any) {
    console.error('❌ Error al verificar credenciales:', error);
    if (error.message) throw new Error(error.message);
    throw new Error('Error de conexión con el servidor');
  }
};

// ==================== ACTUALIZACIONES ====================
export const updateUser = async (userId: string, updates: Partial<UserDataLegacy>): Promise<UserDataLegacy | null> => {
  try {
    const apiUpdates: any = { ...updates };
    delete apiUpdates.password;
    delete apiUpdates.email;
    delete apiUpdates.id;
    delete apiUpdates.createdAt;

    const response = await updateUserProfile(apiUpdates);

    // Actualizar sesión con los nuevos datos
    const currentToken = getCurrentToken();
    if (currentToken) {
      saveUserSession(response.user, currentToken);
    }

    return convertToLegacyFormat(response.user);
  } catch (error) {
    console.error('Error al actualizar usuario:', error);
    return null;
  }
};

export const markAsNotFirstLogin = async (userId: string): Promise<void> => {
  try {
    await markFirstLoginCompleted();

    // Actualizar sesión local
    const currentUser = getCurrentUser();
    const currentToken = getCurrentToken();

    if (currentUser && currentToken) {
      const updatedUser = { ...currentUser, isFirstLogin: false };
      saveUserSession(updatedUser, currentToken);
    }
  } catch (error) {
    console.error('Error al marcar como no primer login:', error);
  }
};

export const updateClientInterestsLegacy = async (userId: string, interests: string[]): Promise<void> => {
  try {
    await updateClientInterests(interests);

    // Actualizar sesión local
    const currentUser = getCurrentUser();
    const currentToken = getCurrentToken();

    if (currentUser && currentToken) {
      const updatedUser = { ...currentUser, interests };
      saveUserSession(updatedUser, currentToken);
    }
  } catch (error) {
    console.error('Error al actualizar intereses:', error);
  }
};

// ==================== COMPATIBILIDAD ====================
export const isUserLoggedIn = (): boolean => isAuthenticated();

// ==================== HEALTH CHECK ====================
export const checkServerHealth = async (): Promise<{
  isReachable: boolean;
  isDatabaseConnected: boolean;
  status: string;
  error?: string;
}> => {
  try {
    const health = await healthCheck();
    return {
      isReachable: health.status === 'OK' || health.status === 'DEGRADED',
      isDatabaseConnected: health.database === 'Connected',
      status: health.status || 'UNKNOWN',
      error: health.error
    };
  } catch (error) {
    console.error('Servidor no disponible:', error);
    return {
      isReachable: false,
      isDatabaseConnected: false,
      status: 'ERROR',
      error: error instanceof Error ? error.message : 'Error desconocido'
    };
  }
};

// ==================== INICIALIZACIÓN AUTOMÁTICA ====================
let sessionInitialized = false;

export const initializeSession = (): { isAuthenticated: boolean; user: UserDataLegacy | null } => {
  if (sessionInitialized) {
    // Si ya se inicializó, solo devolver el estado actual
    const currentUser = getCurrentUser();
    return {
      isAuthenticated: isAuthenticated(),
      user: currentUser ? convertToLegacyFormat(currentUser) : null
    };
  }

  console.log('🚀 Inicializando sesión...');
  sessionInitialized = true;

  const isAuth = isAuthenticated();
  const currentUser = getCurrentUser();

  if (isAuth && currentUser) {
    console.log('✅ Sesión activa encontrada:', currentUser.email);
    return {
      isAuthenticated: true,
      user: convertToLegacyFormat(currentUser)
    };
  } else {
    console.log('ℹ️ No hay sesión activa');
    return {
      isAuthenticated: false,
      user: null
    };
  }
};

// ==================== VERIFICACIÓN INICIAL ====================
checkServerHealth().then((health) => {
  if (!health.isReachable) {
    console.warn('⚠️ No se pudo contactar al backend. Algunas funciones pueden no funcionar correctamente.');
    console.warn('💡 Verifica la ruta /api/v1 y la conectividad del servidor.');
  } else if (!health.isDatabaseConnected) {
    console.warn('⚠️ Backend online en modo degradado: la base de datos está desconectada.');
    console.warn('💡 El registro y el login se reanudarán cuando PostgreSQL local vuelva a estar disponible.');
  } else {
    console.log('✅ Backend y base de datos conectados exitosamente');
  }
});

// La inicialización de sesión se maneja desde App.tsx para evitar múltiples llamadas
